/**
 * Starter Village, surveyed: the village map and the inside of all five of its
 * regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * One file per kingdom, because a kingdom is one piece of work — six maps that
 * have to agree with each other — and because nine authors appending to one
 * file is nine merge conflicts in the same three lines. `towns.ts` and
 * `region-maps.ts` are the barrels that gather these; nothing outside them
 * imports this file.
 *
 * The legend, the invariants and why any of this exists are documented once, in
 * those two barrels. What belongs here is what *this* kingdom's geography is.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const VILLAGE = 'kingdom-starter-village';

/* ========================================================== the village === */

/**
 * Starter Village — the meadow hills, the mill stream and the road out.
 *
 * The geography is the quest line's own, not decoration over it. The stream
 * splits the village: the Counting Meadow and Sum Cottage are the western side
 * a beginner starts on, and the one bridge on the high road is what everything
 * else is reached across. The orchard climbs the hill to the north-east, in
 * rows, because that is what the Orchard Rows quest is about; the Sharing Barn
 * sits at the bottom of the lane by the duck pond; and the Two-Step Trail is a
 * real second way round to Serla's toll gate rather than a card — a road out
 * that takes two moves instead of one, which is the whole of what its
 * mathematics is about.
 *
 * The gate is a *place*, and Serla is standing at it whether or not the seal
 * over the village has ever been mentioned. There is no seal over Starter
 * Village to begin with, and if there were it would hold back her ceremony and
 * not one tile of this map.
 */
const STARTER_VILLAGE: Town = {
  kingdomId: 'kingdom-starter-village',
  caption:
    'The village you wake up in: the meadow, the mill stream, the orchard on the hill, and the toll gate on the road out.',
  width: 26,
  height: 18,
  /* The crossroads on the green, beside the notice board. Arriving in the
     middle of the village is the answer to "where am I" that needs no walking
     first — and where a student is standing is never stored, so this is where
     every visit to the village begins. */
  start: { x: 13, y: 9 },
  rows: [
    'f^^^^^^^^^^^^^^^^^^^^^^^^f',
    'f^^^^^^^^^~^^^^^^^^^^^^^^f',
    'f.........~....fffff.....f',
    'f..f..f...~.ffffffffff.f.f',
    'f.......f.~.ffff#fffff...f',
    'f.........~.ffff#ffff....f',
    'f.f..#...,~,..ff#ff...f..f',
    'f....#...,~,,,,.#......f.f',
    'f....#...,~,,,,.#...f....f',
    '##########=###############',
    'f.....f#.,~,......#..#..#f',
    'f.f....#.,~,......#..#..#f',
    'f......#.,~,......#..####f',
    'f...f.....~..,,,,.#......f',
    'f.........~..,~~,...f....f',
    'f..f...f..~..,~~,.....f..f',
    'ffffffffff~ff,,,,fffffffff',
    'ffffffffff~fffffffffffffff',
  ],
  /**
   * The eight places, in the order a student meets them walking out from the
   * green. Each of the five regions stands at the end of its own lane, so
   * arriving somewhere is the end of a road rather than a scramble across a
   * field — and the two that name no region carry their own words because
   * nothing in the graph names a notice board.
   */
  places: [
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 13, y: 8 },
    { kind: 'road', name: 'The Road Home', emoji: '🗺️', x: 2, y: 9 },
    { kind: 'region', regionId: 'region-counting-meadow', x: 5, y: 6 },
    { kind: 'region', regionId: 'region-sum-cottage', x: 7, y: 12 },
    { kind: 'region', regionId: 'region-times-table-grove', x: 16, y: 4 },
    { kind: 'region', regionId: 'region-sharing-barn', x: 18, y: 13 },
    { kind: 'region', regionId: 'region-two-step-trail', x: 22, y: 12 },
    { kind: 'boss', bossId: 'boss-starter-village', x: 23, y: 9 },
  ],
};

/* ========================================================== the regions === */

/* -------------------------------------------------------------------------- */
/* Counting Meadow                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A walled meadow: the fold, the planted rows, the pond.
 *
 * Counting is in the fold, because counting a flock is the oldest reason anyone
 * ever counted anything; number patterns are out in the rows, where the pattern
 * is a thing you can see down the length of.
 */
const COUNTING_MEADOW: RegionMap = {
  kingdomId: VILLAGE,
  regionId: 'region-counting-meadow',
  caption: 'A drystone wall, a flock in the fold, and rows planted straight enough to see all the way down.',
  width: 16,
  height: 11,
  /* Two steps in from the gate, out in the open where the rows are in sight.
     Arriving on the gate itself makes the first thing the map says "the way
     out is one step south", which is a poor welcome to a meadow. */
  start: { x: 8, y: 8 },
  rows: [
    '%%%%%%%%%%%%%%%%',
    '%..............%',
    '%....""""""".f.%',
    '%.f.."""""""...%',
    '%..............%',
    '%..%%%%%..~~~..%',
    '%..%...%..~~~..%',
    '%..#...%...~...%',
    '%..%%%%%.....f.%',
    '%.....#........%',
    '%%%%%%#%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'counting', x: 5, y: 7 },
    { kind: 'concept', conceptId: 'number-patterns', x: 8, y: 3 },
    { kind: 'road', name: 'The Meadow Gate', emoji: '🏡', x: 6, y: 10 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Sum Cottage                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Two rooms and a garden.
 *
 * The three concepts are placed where the mathematics happens: adding at the
 * hearth, where things are carried; subtracting in the pantry, where things are
 * taken from a shelf and something has to be borrowed from the next one; and
 * fact families at the kitchen table between the two, which is the point of
 * them — the same three numbers, walked either way.
 */
const SUM_COTTAGE: RegionMap = {
  kingdomId: VILLAGE,
  regionId: 'region-sum-cottage',
  caption: 'The hearth where you carry, the pantry where you borrow, and the table between them where they turn out to be the same door.',
  width: 17,
  height: 13,
  /* Inside, past the door: going into a cottage should put you in the kitchen. */
  start: { x: 4, y: 9 },
  rows: [
    '.................',
    '..%%%%%%%%%%%%%..',
    '..%_%%%_%_%%%%%f.',
    '.f%_____%_____%..',
    '..%_____%___%%%..',
    '..%_____%___%%%..',
    '..%_%%________%..',
    '..%_%%__%_____%..',
    '..%_____%_____%f.',
    '.f%_____%_____%..',
    '..%%_%%%%%%%%%%..',
    '....#....."""....',
    '....#....."""....',
  ],
  places: [
    { kind: 'concept', conceptId: 'multi-digit-addition', x: 5, y: 3 },
    { kind: 'concept', conceptId: 'multi-digit-subtraction', x: 11, y: 4 },
    { kind: 'concept', conceptId: 'fact-families', x: 6, y: 7 },
    { kind: 'road', name: 'The Garden Path', emoji: '🏡', x: 4, y: 12 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Times Table Grove                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Seven rows of eight — counted, not decorated.
 *
 * This is the one map where the picture *is* the argument: the Orchard Rows
 * quest is two apprentices shouting about whether it is eight rows of seven or
 * seven rows of eight, and a student who walks the headland and looks down the
 * grove both ways has settled it without being told. The trees are on the odd
 * tiles and the lanes on the even ones, so the array is legible from any tile
 * in it.
 */
const TIMES_TABLE_GROVE: RegionMap = {
  kingdomId: VILLAGE,
  regionId: 'region-times-table-grove',
  caption:
    'Seven rows of eight, inside a wall. Walk the headland and look down it the other way, and it is eight rows of seven and the same orchard.',
  width: 20,
  height: 15,
  start: { x: 1, y: 13 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#.................%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#.................%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#.................%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#.................%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#.................%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#.................%',
    '%#.f.f.f.f.f.f.f.f.%',
    '%#%%%%%%%%%%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'multiplication-facts', x: 4, y: 11 },
    { kind: 'concept', conceptId: 'multiplication-properties', x: 10, y: 7 },
    { kind: 'concept', conceptId: 'multiply-by-tens', x: 16, y: 3 },
    { kind: 'road', name: 'The Headland Gate', emoji: '🏡', x: 1, y: 14 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Sharing Barn                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Stalls, two waggons, and the heap that would not divide.
 *
 * Division's three ideas, each standing on the thing that makes it: the stalls
 * along the back wall are what dividing *is*; the heap at the far end is the
 * remainder, and it is drawn as loose hay rather than as another stall on
 * purpose; and the two waggons are plainly not the same size, which is the
 * whole of multiplicative comparison before anybody writes it down.
 */
const SHARING_BARN: RegionMap = {
  kingdomId: VILLAGE,
  regionId: 'region-sharing-barn',
  caption: 'Stalls to share the harvest into, two waggons that are not the same size, and the heap at the back that would not divide.',
  width: 19,
  height: 13,
  /* Inside the barn door, with the waggons in front of you. */
  start: { x: 9, y: 9 },
  rows: [
    '...................',
    '..%%%%%%%%%%%%%%%..',
    '..%__%_%_%_%_%__%..',
    '..%__%_%_%_%_%__%f.',
    '.f%_____________%..',
    '..%_____________%..',
    '..%_%__%%%___"""%..',
    '..%_%__%%%___"""%..',
    '..%__________"""%..',
    '..%_____________%..',
    '..%%%%%%%_%%%%%%%..',
    '.f.......#.........',
    '.........#......f..',
  ],
  places: [
    { kind: 'concept', conceptId: 'division-meaning', x: 8, y: 3 },
    { kind: 'concept', conceptId: 'division-remainders', x: 14, y: 7 },
    { kind: 'concept', conceptId: 'multiplicative-comparison', x: 6, y: 7 },
    { kind: 'road', name: 'The Barn Yard', emoji: '🏡', x: 9, y: 12 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Two-Step Trail                                                             */
/* -------------------------------------------------------------------------- */

/**
 * A path with a turn in it.
 *
 * The shape is the region's name: nothing on this map is reached by walking in
 * one direction. The first leg runs east along the valley and over the beck;
 * the second turns and climbs back west to the cairn, and it is the cairn — the
 * far end of the second leg — that holds long division. A student who walks it
 * has done a two-step problem with their feet.
 */
const TWO_STEP_TRAIL: RegionMap = {
  kingdomId: VILLAGE,
  regionId: 'region-two-step-trail',
  caption: 'Out of the village, east along the beck, then turn and climb: no leg of this path finishes the journey on its own.',
  width: 18,
  height: 13,
  /* One step up the trail from the stile, so the first thing anybody does here
     is walk further in rather than straight back out. */
  start: { x: 2, y: 10 },
  rows: [
    'f^^^^^^^^^^^^^^^^f',
    'f^^^^^^^^^^^^^^^^f',
    'f.^^^^^....^^^^^.f',
    'f.^^^^^..~.^^^^^.f',
    'f.^^^^^..~.^^^^^.f',
    'f........~.^^^^^.f',
    'f........~#####..f',
    'f.f......~....#..f',
    'f.#######=#####..f',
    'f.#f.....~.......f',
    'f.#......~....f..f',
    'f.#..f...f.......f',
    'ffffffffffffffffff',
  ],
  places: [
    { kind: 'concept', conceptId: 'two-step-problems', x: 5, y: 8 },
    { kind: 'concept', conceptId: 'multiply-multi-digit', x: 14, y: 7 },
    { kind: 'concept', conceptId: 'long-division', x: 10, y: 6 },
    { kind: 'road', name: 'The Trailhead', emoji: '🏡', x: 2, y: 11 },
  ],
};

/* -------------------------------------------------------------------------- */

export const STARTER_VILLAGE_TOWN: Town = STARTER_VILLAGE;

export const STARTER_VILLAGE_REGION_MAPS: readonly RegionMap[] = [
  COUNTING_MEADOW,
  SUM_COTTAGE,
  TIMES_TABLE_GROVE,
  SHARING_BARN,
  TWO_STEP_TRAIL,
];
